import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import AppleSignInButton from "@/components/AppleSignInButton";
import DisclaimerNote from "@/components/DisclaimerNote";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  // Apple's callback bounces back here with a reason when a sign-in fails or
  // is cancelled, so the user is never dumped on a silent login screen.
  useEffect(() => {
    const e = new URLSearchParams(window.location.search).get("apple_error");
    if (!e) return;
    const msg = e === "cancelled" ? "Apple sign-in cancelled."
      : e === "state_mismatch" ? "Apple sign-in expired — please try again."
      : e === "no_email" ? "Apple did not share an email address, so we can't match your account."
      : e === "email_taken" ? "An account already exists for that email. Sign in your usual way, then link Apple under Company Settings."
      : e === "account_closed" ? "This company's Checkmate account has been closed."
      : e.startsWith("link_conflict") ? "That Apple ID is already linked to another account."
      : "Apple sign-in could not be verified. Please try again.";
    toast.error(msg);
    window.history.replaceState({}, "", "/login");
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      await login(email, password);
      toast.success("Signed in");
      nav("/app");
    } catch (e) {
      setErr(e.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-paper flex items-center justify-center p-6 relative">
      <div className="absolute inset-0 grid-noise pointer-events-none" />
      <div className="w-full max-w-md hard-card hard-card-shadow p-8 relative">
        <Link to="/" className="overline mb-8 inline-block hover:underline">← CHECKMATE</Link>
        <h1 className="font-heading font-black text-3xl tracking-tight mb-2">Sign In</h1>
        <p className="text-sm text-muted-foreground mb-6">Access your payroll console.</p>
        <form onSubmit={submit} className="space-y-5" autoComplete="off">
          <div>
            <div className="overline mb-2">Email</div>
            <input data-testid="login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoComplete="off" placeholder="you@company.com" className="w-full px-4 py-3 border-2 border-ink font-mono text-sm focus:outline-none focus:ring-2 focus:ring-ink" />
          </div>
          <div>
            <div className="overline mb-2">Password</div>
            <input data-testid="login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required autoComplete="new-password" placeholder="••••••••" className="w-full px-4 py-3 border-2 border-ink font-mono text-sm focus:outline-none focus:ring-2 focus:ring-ink" />
          </div>
          {err && <div data-testid="login-error" className="border-2 border-bad text-bad font-mono text-xs p-3">{err}</div>}
          <button data-testid="login-submit" type="submit" disabled={busy} className="hard-btn w-full py-3 font-mono uppercase tracking-widest text-sm disabled:opacity-60">
            {busy ? "Signing in…" : "Sign In →"}
          </button>
          <div className="text-center">
            <Link data-testid="login-forgot-password" to="/forgot-password"
              className="font-mono text-xs underline underline-offset-4 hover:text-ink text-muted-foreground">
              Forgot Password?
            </Link>
          </div>
        </form>

        {/* Enterprise SSO Hub — Google (via Emergent-managed Google Auth) + Microsoft Azure AD */}
        <div className="mt-6" data-testid="sso-hub">
          <div className="relative my-4">
            <div className="absolute inset-0 flex items-center"><div className="w-full border-t-2 border-ink/20" /></div>
            <div className="relative text-center"><span className="bg-paper px-3 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Sovereign access</span></div>
          </div>
          <div className="grid grid-cols-1 gap-3">
            <AppleSignInButton />
            <button type="button" data-testid="sso-google-btn" onClick={() => {
              // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
              const redirectUrl = window.location.origin + '/auth/google-callback';
              window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
            }} className="w-full border-2 border-ink bg-ink text-white py-3.5 font-mono uppercase text-sm tracking-widest inline-flex items-center justify-center gap-3 shadow-hard hover:-translate-y-0.5 hover:shadow-hard-lg transition-transform">
              <span className="bg-white p-1 inline-flex"><svg className="w-4 h-4" viewBox="0 0 24 24" aria-hidden="true"><path fill="#EA4335" d="M12 5c1.6 0 3 .5 4.2 1.5l3-3C17.2 1.7 14.8.7 12 .7 7.4.7 3.5 3.4 1.6 7.3l3.5 2.7C6 7.3 8.8 5 12 5z"/><path fill="#34A853" d="M23.3 12.3c0-.8-.1-1.5-.2-2.3H12v4.3h6.3c-.3 1.5-1.1 2.8-2.4 3.6l3.7 2.9c2.2-2 3.7-5 3.7-8.5z"/><path fill="#4A90E2" d="M5.1 14.3l-3.5 2.7C3.5 20.9 7.5 23.3 12 23.3c3.2 0 5.9-1 7.8-2.9l-3.7-2.9c-1 .7-2.3 1.1-4.1 1.1-3.2 0-5.9-2.2-6.9-5.2z"/><path fill="#FBBC05" d="M1.6 7.3C1.1 8.6 .7 10 .7 11.5s.4 3 1 4.3l3.5-2.7c-.3-.9-.5-1.9-.5-2.9 0-1.1.2-2 .4-2.9L1.6 7.3z"/></svg></span>
              Sign in with Google
            </button>
            <button type="button" data-testid="sso-microsoft-btn" onClick={() => {
              // Bounces to backend which owns the OAuth handshake, then Microsoft, then back through /api/auth/microsoft/callback → /app
              const base = process.env.REACT_APP_BACKEND_URL || "";
              window.location.href = `${base}/api/auth/microsoft/login`;
            }} className="w-full border-2 border-ink py-2.5 font-mono uppercase text-xs tracking-widest inline-flex items-center justify-center gap-2 hover:bg-secondary transition-colors">
              <svg className="w-4 h-4" viewBox="0 0 21 21" aria-hidden="true"><rect x="1" y="1" width="9" height="9" fill="#F25022"/><rect x="11" y="1" width="9" height="9" fill="#7FBA00"/><rect x="1" y="11" width="9" height="9" fill="#00A4EF"/><rect x="11" y="11" width="9" height="9" fill="#FFB900"/></svg>
              Microsoft Azure AD
            </button>
          </div>
          <div className="mt-3 font-mono text-[10px] text-muted-foreground text-center">Corporate SSO available on Enterprise-Grade plans. <Link to="/app/company" className="underline">Enforce SSO only</Link> in Company Settings.</div>
        </div>

        <div className="mt-6 pt-6 border-t-2 border-ink text-sm">
          <div className="text-muted-foreground">New here?</div>
          <Link data-testid="login-to-register" to="/register" className="underline underline-offset-4 font-medium">Create a company account →</Link>
        </div>
        <DisclaimerNote />
      </div>
    </div>
  );
}
